import React from "react";
import { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { postWithFormData } from "../../helpers/api.helper";
import { RiArrowRightSLine } from "react-icons/ri";
import TermsModal from "../../components/TermsModal";
import {
  AiOutlineClose,
  AiOutlineEye,
  AiOutlineEyeInvisible,
} from "react-icons/ai";
import { DropdownOption, StepPropsType } from "../../types";
import FullPageLoader from "../../components/FullPageLoader";
import { PrimaryDropdownKey } from "../../types/user.creation";
import { dropdownOptions } from "../../utils/user.creation.constants";
import ProfileImageUpload from "../../components/ProfileImageUpload";
import ProfileImageCropModal from "../../components/ProfileImageCropModal";
import { useLanguage } from "../../context/LanguageContext";
import { formatCreatedBy } from "../../utils/createdBy";
import { getCountries, getCountryCallingCode } from "react-phone-number-input";

type Step1Props = StepPropsType & {
  otherBureauId?: string;
  whose?: "own" | "mediator";
  extractedData?: {
    gender?: string;
    fullName?: string;
  };
};

const Step1 = ({
  bureauId,
  setCurrentStep,
  steps,
  currentStep,
  setProfileId,
  whose,
  otherBureauId,
  extractedData,
  setCompletedSteps,
}: Step1Props) => {
  const { t } = useLanguage();
  const isPublicSelfRegistration = !bureauId && !otherBureauId;
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setLoading] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [countryCode, setCountryCode] = useState("+91");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [modalData, setModalData] = useState<{
    key: PrimaryDropdownKey;
    options: DropdownOption[];
  } | null>(null);
  const [selectedOptions, setSelectedOptions] = useState<
    Record<PrimaryDropdownKey, DropdownOption>
  >({
    gender: { value: "", label: t("step1.selectGender") },
    createdBy: {
      value: isPublicSelfRegistration ? "super_admin" : "self",
      label: isPublicSelfRegistration ? "Super Admin" : t("step1.self"),
    },
    citizenship: { value: "Indian", label: t("step1.indianProfile") },
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [showTerms, setShowTerms] = useState(false);

  const [rawImage, setRawImage] = useState<File | null>(null);
  const [image, setImage] = useState<File | null>(null); // cropped final
  const [showImageCrop, setShowImageCrop] = useState(false);
  const [pendingSubmit, setPendingSubmit] = useState(false);

  const [isImageProcessing, setIsImageProcessing] = useState(false);
  const [isCropProcessing, setIsCropProcessing] = useState(false);
  const submitLockRef = useRef(false);
  const modalFlowLocked =
    showImageCrop && (isImageProcessing || isCropProcessing || isLoading);

  useEffect(() => {
    document.body.dataset.cropModalProcessing = modalFlowLocked
      ? "true"
      : "false";

    return () => {
      delete document.body.dataset.cropModalProcessing;
    };
  }, [modalFlowLocked]);

  useEffect(() => {
    if (extractedData) {
      if (extractedData.gender)
        setSelectedOptions((prev) => ({
          ...prev,
          gender: {
            value: extractedData.gender.toLowerCase(),
            label: extractedData.gender,
          },
        }));
      if (extractedData.fullName)
        toast.info(`Prefilled: ${extractedData.fullName}`);
    }
  }, [extractedData]);

  const handleSubmit = async () => {
    if (
      isLoading ||
      isImageProcessing ||
      isCropProcessing ||
      submitLockRef.current
    ) {
      return;
    }

    // validations (unchanged)
    if (!selectedOptions.gender.value) {
      toast.error("Please select gender");
      return;
    }
    if (!phoneNumber || phoneNumber.length !== 10) {
      toast.error("Please enter a valid 10-digit mobile number");
      return;
    }
    if (!password) {
      toast.error("Please enter password");
      return;
    }
    if (!fullName) {
      toast.error("Please enter full Name");
      return;
    }

    // Step 1: image must be selected
    if (!rawImage) {
      toast.error("Please upload profile image");
      return;
    }

    // Step 2: force crop
    if (!image) {
      submitLockRef.current = true;
      setPendingSubmit(true);
      setShowImageCrop(true);
      return;
    }

    // If image exists → continue actual submit
    await submitStep1(image);
  };

  const submitStep1 = async (
    finalImage: File,
    allowLockedSubmit = false,
  ): Promise<boolean> => {
    if ((submitLockRef.current && !allowLockedSubmit) || isLoading) {
      return false;
    }

    submitLockRef.current = true;
    setLoading(true);

    const formData = new FormData();
    formData.append("visibleToAll", "false");
    formData.append("gender", selectedOptions.gender.value);
    formData.append(
      "createdBy",
      whose === "mediator"
        ? "other_mediator"
        : isPublicSelfRegistration
          ? "super_admin"
          : selectedOptions.createdBy.value,
    );
    formData.append("citizenship", selectedOptions.citizenship.value);
    formData.append("mobileNumber", phoneNumber);
    formData.append("countryCode", countryCode);
    formData.append("password", password);
    formData.append("fullName", fullName);
    //@ts-ignore
    formData.append("bureauId", otherBureauId ? otherBureauId : bureauId);
    formData.append("paymentStatus", "Free Profile");
    // ✅ image guaranteed here
    // 🔥 USE DIRECT FILE
    formData.append("image", finalImage);
    formData.append("step1", "1");

    try {
      const res = await postWithFormData("/user/register", formData);
      if (res.success) {
        setProfileId?.(res.data);
        setCompletedSteps?.((prev) =>
          prev.includes(currentStep) ? prev : [...prev, currentStep],
        );
        setCurrentStep(currentStep + 1);
        return true;
      } else {
        toast.error(res.message || "Registration failed");
      }
    } catch (err: unknown) {
      toast.error("Registration failed");
    } finally {
      submitLockRef.current = false;
      setLoading(false);
      setPendingSubmit(false);
    }

    return false;
  };

  const openModal = (key: PrimaryDropdownKey) => {
    setModalData({ key, options: dropdownOptions[key] });
  };

  const closeModal = () => {
    setModalData(null);
    setSearchTerm("");
  };

  const handleSelect = (selectedOption: DropdownOption) => {
    if (!modalData) return;

    setSelectedOptions((prev) => ({
      ...prev,
      [modalData.key]: selectedOption,
    }));
    closeModal();
  };
  const getFieldLabel = (key: PrimaryDropdownKey): string => {
    const labelMap: Record<PrimaryDropdownKey, string> = {
      gender: t("step1.gender"),
      createdBy: t("step1.createdBy"),
      citizenship: t("Citizenship"),
    };
    return labelMap[key] || key.replace(/_/g, " ");
  };
  return (
    <>
      {isLoading && <FullPageLoader />}
      <div className="overflow-x-hidden p-2 sm:p-6 md:p-8">
        <ProfileImageUpload
          setImage={(file) => {
            setRawImage(file); // ⛔ do NOT set final image yet
            setImage(null);
          }}
          onProcessingChange={setIsImageProcessing}
          disabled={showImageCrop || isCropProcessing || isLoading}
        />

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t("Full Name")} *
          </label>
          <input
            type="text"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            required
          />
        </div>
        {(Object.keys(dropdownOptions) as PrimaryDropdownKey[]).map((key) => {
          if (whose == "mediator" && key == "createdBy") return null;
          if (isPublicSelfRegistration && key === "createdBy") return null;
          return (
            <div key={key} className="mt-4">
              <label className="block text-gray-800 font-semibold mb-1 capitalize">
                {getFieldLabel(key)}
              </label>
              <div
                onClick={() => openModal(key)}
                className="flex items-center justify-between px-4 py-3 border rounded-lg cursor-pointer hover:shadow-md transition-all hover:bg-gray-100"
              >
                <span className="text-gray-700">
                  {(key === "createdBy"
                    ? formatCreatedBy(selectedOptions[key]?.value)
                    : selectedOptions[key]?.label) ||
                    `${t("step1.select")} ${getFieldLabel(key)}`}
                </span>
                <RiArrowRightSLine className="text-2xl text-gray-500" />
              </div>
            </div>
          );
        })}
        {/* Contact Number */}
        <div className="mt-4">
          <label className="block text-gray-800 font-semibold mb-1">
            {whose == "mediator"
              ? t("Mediator Contact Number")
              : t("Contact Number")}
          </label>
          <div className="flex min-w-0 gap-3 overflow-x-hidden">
            <select
              value={countryCode}
              onChange={(e) => setCountryCode(e.target.value)}
              className="w-24 shrink-0 border border-gray-300 rounded-lg px-2 py-2 bg-white focus:ring-2 focus:ring-red-400 sm:w-28"
            >
              {getCountries().map((country) => (
                <option
                  key={country}
                  value={`+${getCountryCallingCode(country)}`}
                >
                  +{getCountryCallingCode(country)}
                </option>
              ))}
            </select>
            <input
              type="tel"
              placeholder="Ex: 98XXXXXXXX"
              value={phoneNumber}
              onChange={(e) => {
                const digitsOnly = e.target.value.replace(/\D/g, "");
                setPhoneNumber(digitsOnly);
              }}
              className="min-w-0 flex-1 border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-red-400"
              maxLength={10}
              inputMode="numeric"
            />
          </div>
        </div>
        {/* Password */}
        <div className="mt-4">
          <label className="block text-gray-800 font-semibold mb-1">
            {t("Password")}
          </label>
          <div className="relative">
            <input
              type={showPassword ? "text" : "password"}
              value={password}
              className="w-full border border-gray-300 rounded-lg px-4 py-2 pr-10 focus:ring-2 focus:ring-red-400"
              onChange={(e) => setPassword(e.target.value)}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-red-500"
            >
              {showPassword ? (
                <AiOutlineEyeInvisible size={20} />
              ) : (
                <AiOutlineEye size={20} />
              )}
            </button>
          </div>
          {/* <p className="text-xs text-gray-500 mt-1">
          Auto Password: <span className="font-medium">{password}</span>
        </p> */}
        </div>
        {/* Terms */}
        <div className="flex items-center gap-2 mt-3">
          <input
            id="terms"
            type="checkbox"
            defaultChecked
            className="accent-red-500 w-4 h-4"
          />
          <label
            htmlFor="terms"
            className="text-gray-700 text-sm cursor-pointer"
            onClick={() => setShowTerms(true)}
          >
            I agree to the{" "}
            <span className="text-red-500 font-semibold hover:underline">
              Terms and Conditions
            </span>
          </label>
        </div>
        {/* Action Buttons */}
        <div className="flex flex-col-reverse sm:flex-row gap-3 pt-6">
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isLoading || isImageProcessing || isCropProcessing}
            className={`flex-1 rounded-lg px-6 py-3.5 font-semibold shadow-lg transition-all ${
              isLoading || isImageProcessing || isCropProcessing
                ? "cursor-not-allowed bg-gray-300 text-gray-500"
                : "bg-gradient-to-r from-pink-500 to-purple-600 text-white hover:from-pink-600 hover:to-purple-700"
            }`}
          >
            {isImageProcessing
              ? "Optimizing image..."
              : isLoading
                ? "Submitting..."
                : currentStep === steps.length
                  ? "Submit"
                  : "Next Step"}
          </button>
        </div>
        {modalData && (
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex justify-center items-center z-50">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 p-6 relative max-h-[85vh] overflow-y-auto">
              <button
                className="absolute top-3 right-3 text-gray-500 hover:text-red-500"
                onClick={() => {
                  setModalData(null);
                  setSearchTerm("");
                }}
              >
                <AiOutlineClose size={22} />
              </button>
              <h2 className="text-lg font-bold mb-4 text-center capitalize">
                Select {modalData.key.replace(/_/g, " ")}
              </h2>
              <input
                type="text"
                placeholder={`Search ${modalData.key}`}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg mb-4 focus:ring-2 focus:ring-red-400"
              />
              <div className="max-h-64 overflow-y-auto divide-y">
                {modalData.options
                  .filter((option) =>
                    option.label
                      .toLowerCase()
                      .includes(searchTerm.toLowerCase()),
                  )
                  .map((option, index) => (
                    <button
                      key={option.value}
                      onClick={() => handleSelect(option)}
                      className="block w-full text-left px-4 py-2 hover:bg-red-50 transition"
                    >
                      {option.label}
                    </button>
                  ))}
              </div>
            </div>
          </div>
        )}
        {showTerms && (
          <TermsModal
            isOpen={showTerms}
            bureauId={otherBureauId ? otherBureauId : bureauId}
            onClose={() => setShowTerms(false)}
          />
        )}
      </div>

      {showImageCrop && rawImage && (
        <ProfileImageCropModal
          sourceFile={rawImage}
          onProcessingChange={setIsCropProcessing}
          onConfirm={async (croppedFile: File) => {
            setImage(croppedFile);
            setShowImageCrop(false);

            if (pendingSubmit) {
              await submitStep1(croppedFile, true);
            }

            setPendingSubmit(false);
          }}
          onClose={() => {
            if (isImageProcessing || isCropProcessing || isLoading) {
              return;
            }

            setShowImageCrop(false);
            setPendingSubmit(false);
            submitLockRef.current = false;
          }}
        />
      )}
    </>
  );
};

export default Step1;
